import type { PreBookingResponse } from '~/api/prebooking'
import type {
  PrebookingDataLayerMetaValues,
  PrebookingDataLayerObject,
  PrebookingDataLayerValues,
} from '~/types/datalayer'
import { Tracking } from '@meeseeks/centaur-tracking'
import * as Sentry from '@sentry/vue'
import { storeToRefs } from 'pinia'
import { PreBookingRequestVehicleCategoryEnum } from '~/api/prebooking'
import { useOfferAdditionalOptions } from '~/composables/offer-additional-options'
import { isRunningOnFree2MoveDomain } from '~/composables/tracking/tracking-scripts'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'

import { useVehiclesStore } from '~/stores/vehicles'

const tracking = new Tracking()
const PREBOOKING_EVENT_NAME = 'prebooking_success'

export function useDataLayer() {
  function pushToDataLayer<T = any>(payload: T) {
    tracking.pushToDataLayer(payload)
  }

  function pushPendingEvents() {
    tracking.sendPendingCustomEventsToDatalayer()
  }

  function pushSuccessfulPrebookingEvent(prebookingResponse: PreBookingResponse) {
    try {
      pushToDataLayer<PrebookingDataLayerObject>({
        ...buildSuccessfulPrebookingDataLayerMetaEvent(),
        ...buildPrebookingDataLayerObject(prebookingResponse),
      })
    }
    catch (e) {
      Sentry.captureException(e)
    }
  }

  return {
    pushPendingEvents,
    pushSuccessfulPrebookingEvent,
  }
}

function buildSuccessfulPrebookingDataLayerMetaEvent(): PrebookingDataLayerMetaValues {
  return {
    event: isRunningOnFree2MoveDomain() ? 'ga4_event' : 'ce_prebooking_success',
    event_name: PREBOOKING_EVENT_NAME,
  }
}

function buildPrebookingDataLayerObject(prebookingResponse: PreBookingResponse): PrebookingDataLayerValues {
  /*
  Since tracking is loaded before the main component is mounted, we need to create store references during runtime.
  At this point, all information is available in the stores.
   */
  const extraStore = useExtrasStore()
  const { option: plusOption } = useOfferAdditionalOptions('plus_insurance')
  const { option: crossBorderOption } = useOfferAdditionalOptions(`cross_border_${useLocationStore().city.country}`, '.')
  const { option: comfortOption } = useOfferAdditionalOptions('cleaned', '\n', 'refueled')
  const { city } = storeToRefs(useLocationStore())
  const category = useVehiclesStore().getVehicleCategoryData()?.vehicleCategory

  return {
    prebooking_id: prebookingResponse.preBookingUuid,
    country: city.value.country,
    location: city.value.id,
    value: (useOfferStore().selectedOffer?.price ?? 0) + useExtrasStore().totalExtraPrice,
    currency: useOfferStore().selectedOffer?.currency ?? '',
    prebooking_vehicle_category: PreBookingRequestVehicleCategoryEnum[category!],
    prebooking_insurance: extraStore.propertyChosen(plusOption.value?.id) ? 'plus_coverage' : 'basic_coverage',
    prebooking_extras: {
      comfort_package: extraStore.propertyChosen(comfortOption.value?.id) ? 'true' : 'false',
      cross_border: extraStore.propertyChosen(crossBorderOption.value?.id) ? 'true' : 'false',
      additional_driver: prebookingResponse.additionalDriverCode ? 'true' : 'false',
    },
    prebooking_new_user: prebookingResponse.shouldRegister ? 'true' : 'false',
  }
}
